///
/// EarthBrowser
///
/// Created by Matt Giger on 9/15/2026
/// Copyright (c) 2026 Geodata Labs
///

import { DesignColors } from "@/earthbrowser/design-system/DesignColors"
import { PolygonGeometry } from "@/earthbrowser/entities/geometries/PolygonGeometry"
import { PolylineNode } from "@/earthbrowser/entities/Polyline/PolylineNode"
import { SimpleNode } from "@/earthbrowser/entities/SimpleNode/SimpleNode"
import { geographic } from "@/earthbrowser/utils/geographic"
import { isMemoryConstrainedDevice } from "@/earthbrowser/utils/MemoryLog"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { rgba, type color } from "@stovokor/math/color"
import { trig } from "@stovokor/math/math"
import { vec2 } from "@stovokor/math/vector"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { simplifyRing } from "../wildfires/simplifyRing"
import { type AlertSeverity } from "./AlertsTable"

// The outline floats slightly above the fill so it always wins the depth
// test against its own polygon
const cOutlineLiftKm = 0.15

const cOutlineWidth = 1.5
const cOutlineWidthFocused = 3

// Alpha multiplier for alerts not yet in effect (shown during the lead
// window before their onset)
const cUpcomingAlphaFactor = 0.45

// Ring simplification tolerance: NWS storm polygons are already coarse, but
// ECCC forecast-zone polygons carry coastline-level detail that's invisible
// at the zooms where county/zone-scale alerts render
const cSimplifyToleranceM = 60
const cSimplifyToleranceConstrainedM = 120

interface GeoJSONPolygon {
	type: "Polygon"
	coordinates: number[][][]
}

interface GeoJSONMultiPolygon {
	type: "MultiPolygon"
	coordinates: number[][][][]
}

export type AlertGeometry = GeoJSONPolygon | GeoJSONMultiPolygon

/**
 * Filled alert polygon(s) plus outline for one weather alert, colored by
 * CAP severity and draped just above the terrain (the caller passes an
 * altitude computed from the terrain under the alert area). Same
 * fill/outline construction as WildfirePerimeterNode: poly2tri fills are
 * best-effort, degenerate rings fall back to outline-only.
 */
export class AlertAreaNode extends NodeBase {
	private readonly fills: SimpleNode[] = []
	private readonly outlines: PolylineNode[] = []
	private readonly baseFillColor: color
	private readonly baseOutlineColor: color

	constructor(context: StovokorContext, geometry: AlertGeometry, severity: AlertSeverity, altitudeKm: number) {
		super()
		const fillColor = AlertAreaNode.fillColor(severity)
		const outlineColor = AlertAreaNode.outlineColor(severity)
		this.baseFillColor = fillColor
		this.baseOutlineColor = outlineColor
		const outlineAltitude = altitudeKm + cOutlineLiftKm

		const polygons: number[][][][] =
			geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates

		const toleranceM = isMemoryConstrainedDevice()
			? cSimplifyToleranceConstrainedM
			: cSimplifyToleranceM

		for (const sourceRings of polygons) {
			const rings = sourceRings.map(r => simplifyRing(r, toleranceM))
			const outer = rings[0]
			if (!outer || outer.length < 4) continue

			// Filled interior from the outer ring (holes are rare in alert
			// areas and poly2tri hole support is disabled engine-wide)
			try {
				const ringVec2 = outer.map(([lon, lat]) => new vec2(lon, lat))
				const bounds = {
					minLon: Math.min(...outer.map(p => p[0])),
					minLat: Math.min(...outer.map(p => p[1])),
					maxLon: Math.max(...outer.map(p => p[0])),
					maxLat: Math.max(...outer.map(p => p[1])),
				}
				const fillGeometry = new PolygonGeometry(context, [ringVec2], bounds, altitudeKm)
				const fill = new SimpleNode(context, fillGeometry, fillColor)
				this.fills.push(fill)
				this.addNode(fill)
			} catch {
				// poly2tri can throw on self-intersecting rings; keep the outline
			}

			// Outline every ring (outer + holes) so complex areas read correctly
			for (const ring of rings) {
				if (!ring || ring.length < 4) continue
				const positions = ring.map(([lon, lat]) =>
					new geographic(lon * trig.rad, lat * trig.rad, outlineAltitude).cartesian
				)
				const outline = new PolylineNode(context, {
					positions,
					width: cOutlineWidth,
					color: outlineColor,
					loop: true,
				})
				this.outlines.push(outline)
				this.addNode(outline)
			}
		}
	}

	/** Thicken the outline while the alert is selected */
	public setFocused(focused: boolean): void {
		const width = focused ? cOutlineWidthFocused : cOutlineWidth
		for (const outline of this.outlines) {
			outline.updateWidth(width)
		}
	}

	/** Dim the area while the alert is not yet in effect */
	public setUpcoming(upcoming: boolean): void {
		const factor = upcoming ? cUpcomingAlphaFactor : 1
		const fill = this.baseFillColor
		const outline = this.baseOutlineColor
		const fillColor = rgba(fill.r, fill.g, fill.b, fill.a * factor)
		const outlineColor = rgba(outline.r, outline.g, outline.b, outline.a * factor)
		for (const node of this.fills) {
			node.color = fillColor
		}
		for (const node of this.outlines) {
			node.updateColor(outlineColor)
		}
	}

	private static fillColor(severity: AlertSeverity) {
		switch (severity) {
			case "Extreme": return DesignColors.ALERT_FILL_EXTREME
			case "Severe": return DesignColors.ALERT_FILL_SEVERE
			case "Moderate": return DesignColors.ALERT_FILL_MODERATE
			default: return DesignColors.ALERT_FILL_MINOR
		}
	}

	private static outlineColor(severity: AlertSeverity) {
		switch (severity) {
			case "Extreme": return DesignColors.ALERT_OUTLINE_EXTREME
			case "Severe": return DesignColors.ALERT_OUTLINE_SEVERE
			case "Moderate": return DesignColors.ALERT_OUTLINE_MODERATE
			default: return DesignColors.ALERT_OUTLINE_MINOR
		}
	}
}
